import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { checkoutApi } from '../api/client';

export default function CheckoutPending() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Paid-only numbering: prefer ?ref=pending_xxxx (assigned at checkout).
  // Legacy fallback to ?orderNumber=... so already-issued Mollie redirects
  // mid-flight still resolve. Either value is accepted by the backend
  // /api/orders/{ref|number}/status endpoint.
  const pendingRef = searchParams.get('ref');
  const legacyOrderNumber = searchParams.get('orderNumber');
  const lookupRef = pendingRef || legacyOrderNumber;
  const token = searchParams.get('token');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!lookupRef) { navigate('/'); return; }
    const tokenQS = token ? `?token=${encodeURIComponent(token)}` : '';

    let tries = 0;
    const MAX_TRIES = 12; // 12 x 3s = 36s max

    const poll = async () => {
      tries++;
      setAttempts(tries);
      try {
        const { data } = await checkoutApi.getOrderStatus(lookupRef, token);
        // Once the order is confirmed AND the canonical orderNumber is
        // assigned, redirect to the canonical confirmation URL. Until
        // the canonical number lands (webhook may still be in flight)
        // we keep polling.
        if (data.status === 'confirmed' && data.orderNumber) {
          navigate(`/order/${data.orderNumber}/confirmation${tokenQS}`);
          return;
        }
        if (['cancelled', 'refunded'].includes(data.status)) {
          navigate('/checkout/failed');
          return;
        }
      } catch (e) {
        // Polling failures are transient; the next interval or webhook will update status.
      }
      if (tries >= MAX_TRIES) {
        // Last-chance redirect — prefer the canonical orderNumber if
        // we already saw it on a prior poll; otherwise stay on the
        // pending reference so the customer is not handed an
        // enumerable canonical URL prematurely.
        try {
          const { data } = await checkoutApi.getOrderStatus(lookupRef, token);
          const target = data.orderNumber || lookupRef;
          navigate(`/order/${target}/confirmation${tokenQS}`);
        } catch {
          navigate(`/order/${lookupRef}/confirmation${tokenQS}`);
        }
      }
    };

    const interval = setInterval(poll, 3000);
    poll();
    return () => clearInterval(interval);
  }, [lookupRef, token, navigate]);

  return (
    <div data-testid="checkout-pending" className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
      <Loader2 className="w-10 h-10 text-primary animate-spin mb-6" />
      <h1 className="font-serif text-3xl font-light text-foreground mb-2">Betaling verwerken</h1>
      <p className="text-muted-foreground text-sm">Even geduld, we controleren uw betaling...</p>
      {attempts > 3 && (
        <p className="text-xs text-muted-foreground mt-4">Dit kan soms even duren. Sluit dit venster niet.</p>
      )}
    </div>
  );
}
